import React, { useState, lazy, Suspense } from 'react';
import { Form, FormInstance } from '@lightbeamhealth/blueprint-form';
import { faPencil, faSave, faTrash } from '@fortawesome/pro-solid-svg-icons';

import { Col, Container, Row } from '../../../grid';
import Icon from '../../atoms/icon';
import Button from '../../molecules/button';
import Modal from '../../organisms/modal';
import Toast from '../../organisms/toast';

import { IPatient, SavePatient } from '../../../libs/patient';

import { useTriageUpdater } from '../../../contexts/triage/updater';
import { useMetaData } from '../../../contexts/metadata';

const PatientNoteEditor = lazy(
  () => import('../../organisms/patient-note-editor/PatientDescriptionEditor')
);

interface DescriptionFormData {
  note: string;
  descriptionHtml?: string; // Patient note HTML content
}

interface EditDescriptionFormProps {
  note?: string;
  onSubmit: (data: DescriptionFormData) => any;
  onClose?: () => any;
  isLoading?: boolean;
}

const EditDescriptionForm: React.FC<EditDescriptionFormProps> = ({
  note,
  onSubmit,
  onClose,
  isLoading
}) => {
  const form = new FormInstance<DescriptionFormData>({
    note: note ? note : '',
    descriptionHtml: note ? note : ''
  });

  const removeDescription = () => {
    onSubmit({
      note: '',
      descriptionHtml: ''
    });
  };

  return (
    <Form form={form} onSubmit={onSubmit}>
      {(props) => {
        return (
          <>
            <Suspense fallback={<div>Loading editor...</div>}>
              <PatientNoteEditor
                value={props.values.descriptionHtml || ''}
                onChange={(html) =>
                  props.setFieldValue('descriptionHtml', html)
                }
                placeholder="Enter patient description..."
                maxLength={180}
                disabled={isLoading}
                className="mt-2 mb-2"
              />
            </Suspense>
            <Container
              fluid
              className="mb-4"
              style={{
                padding: 0
              }}
            >
              <Row>
                <Col>
                  <Button
                    className="text-small mr-4"
                    disabled={isLoading}
                    icon={<Icon icon={faSave} className="mr-3" />}
                    intent="primary"
                    size="small"
                    text="Save"
                    type="submit"
                  />
                  <Button
                    className="text-small mr-4"
                    onClick={onClose}
                    size="small"
                    text="Cancel"
                  />
                </Col>
                <Col md="content">
                  <Button
                    className="text-small"
                    disabled={isLoading}
                    icon={<Icon icon={faTrash} className="mr-3" />}
                    intent="danger"
                    onClick={removeDescription}
                    size="small"
                    text="Remove"
                  />
                </Col>
              </Row>
            </Container>
          </>
        );
      }}
    </Form>
  );
};

interface EditDescriptionProps {
  patient: IPatient;
}

const EditDescription: React.FC<EditDescriptionProps> = ({ patient }) => {
  const { isReadOnly } = useMetaData();
  const { handleUpdatePatient } = useTriageUpdater();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const onSubmit = (data: DescriptionFormData) => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    SavePatient({
      id: patient.id,
      descriptionHtml: data.descriptionHtml
    })
      .then((newPatient) => {
        setIsLoading(false);
        setIsEditing(false);
        Toast({
          intent: 'success',
          message: 'The description has been updated!'
        });
        handleUpdatePatient(newPatient);
      })
      .catch((error) => {
        setIsLoading(false);
        Modal.error({
          title: 'Error Saving Description',
          content: error.message || error
        });
      });
  };
  return (
    <div className="text-small">
      <span className="uppercase font-bold mr-2">Description</span>
      {isEditing ? (
        <EditDescriptionForm
          note={patient.note}
          onSubmit={onSubmit}
          onClose={() => setIsEditing(false)}
          isLoading={isLoading}
        />
      ) : (
        <>
          {patient.note ? (
            <div
              className="mb-4"
              dangerouslySetInnerHTML={{ __html: patient.note }}
            />
          ) : (
            <span className="bp5-text-muted">NONE</span>
          )}
          {!isReadOnly && (
            <Button
              bare
              className="ml-3 text-small pa-0"
              icon={<Icon icon={faPencil} className="mr-2" />}
              intent="primary"
              variant="minimal"
              onClick={() => setIsEditing(true)}
              size="small"
              text={
                !patient.note || patient.note.trim().length === 0
                  ? 'Add'
                  : 'Edit'
              }
            />
          )}
        </>
      )}
    </div>
  );
};

export default EditDescription;
